/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as MsDyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { ILxsHeroHeaderViewProps } from './lxs-hero-header';

const BACKGROUND_IMAGE_VIEWPORTS = {
    xs: { q: 'w=576&m=6&q=80', w: 576, h: 0 },
    sm: { q: 'w=768&m=6&q=80', w: 768, h: 0 },
    md: { q: 'w=992&m=6&q=80', w: 992, h: 0 },
    lg: { q: 'w=1440&m=6&q=80', w: 1440, h: 0 },
    xl: { q: 'w=1920&m=6&q=80', w: 1920, h: 0 }
};

export default (props: ILxsHeroHeaderViewProps) => {
    const CLASS_NAME = 'lxs-hero-header';
    const { config, context, slots } = props;
    const { backgroundImage, preHeading, noAuthHeading, authHeading, noAuthBodyText, authBodyText } = config;
    const buttons = slots?.buttons;
    const isAuthenticated = context.request.user.isAuthenticated;
    const heading = isAuthenticated ? authHeading : noAuthHeading;
    const bodyText = isAuthenticated ? authBodyText : noAuthBodyText;

    const backgroundImageSettings = {
        ...backgroundImage?.imageSettings,
        viewports: BACKGROUND_IMAGE_VIEWPORTS,
        disableLazyLoad: true
    } as MsDyn365.IImageSettings;

    const handleHeadingTextChange = (event: MsDyn365.ContentEditableEvent) =>
        ((isAuthenticated ? config.authHeading : config.noAuthHeading)!.headingText = event.target.value);
    const handlePreHeadingTextChange = (event: MsDyn365.ContentEditableEvent) => (config.preHeading.headingText = event.target.value);
    const handleBodyTextChange = (event: MsDyn365.ContentEditableEvent) =>
        isAuthenticated ? (config.authBodyText = event.target.value) : (config.noAuthBodyText = event.target.value);

    return (
        <div className={CLASS_NAME}>
            <div className={`${CLASS_NAME}__background`}>
                {backgroundImage && (
                    <MsDyn365.Image
                        {...backgroundImage}
                        className={`${CLASS_NAME}__image`}
                        gridSettings={context.request.gridSettings!}
                        imageSettings={backgroundImageSettings}
                    />
                )}
            </div>

            <div className={`container ${CLASS_NAME}__content-container`}>
                <div className={`${CLASS_NAME}__content`}>
                    {preHeading?.headingText && (
                        <MsDyn365.Text
                            className={`${CLASS_NAME}__content-pre-heading`}
                            text={preHeading.headingText}
                            tag={preHeading.tag || 'h2'}
                            editProps={{ onEdit: handlePreHeadingTextChange, requestContext: context.request }}
                        />
                    )}

                    {heading?.headingText && (
                        <div className={`${CLASS_NAME}__content-heading-wrapper`}>
                            <MsDyn365.Text
                                className={`${CLASS_NAME}__content-heading`}
                                text={heading.headingText}
                                tag={heading.tag || 'h1'}
                                editProps={{ onEdit: handleHeadingTextChange, requestContext: context.request }}
                            />
                            {isAuthenticated && context.request.user.firstName && (
                                <MsDyn365.Text
                                    className={`${CLASS_NAME}__content-heading-name`}
                                    text={context.request.user.firstName}
                                    tag={heading.tag || 'h1'}
                                />
                            )}
                        </div>
                    )}

                    {bodyText && (
                        <MsDyn365.RichTextComponent
                            className={`${CLASS_NAME}__content-body`}
                            text={bodyText}
                            editProps={{ onEdit: handleBodyTextChange, requestContext: context.request }}
                        />
                    )}

                    <div className={`${CLASS_NAME}__buttons`}>
                        {buttons?.map((button, index) => (
                            <div key={index} className={`${CLASS_NAME}__buttons-item`}>
                                {button}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
